import cannedSendToAiJson from './data/issue-chat-send-to-ai.json';
import {
  applyWatchCompanyImport,
  hasImportErrors,
  parseWatchCompanyImportJson,
} from './importWatchCompanies';
import { createDraftWatchCompany, nextWatchCompanyId } from './watchTowerSelectors';

import type { ImportIssue, WatchCompanyImportFile } from './importWatchCompanies';
import type {
  WatchFinding,
  WatchIssueChat,
  WatchIssueChatMessage,
  WatchTowerMockState,
} from './types';

export const MOCK_OPERATOR = 'sales.demo@attaima.example';

export type AddKnownListError =
  | 'same_company'
  | 'missing_buyer'
  | 'missing_source'
  | 'missing_finding'
  | 'invalid_country'
  | 'missing_name'
  | 'missing_uid';

export type AddKnownListResult =
  | { ok: true, state: WatchTowerMockState, createdLink: boolean, createdFlag: boolean }
  | { ok: false, error: AddKnownListError };

export interface NewBuyerInput {
  name: string,
  uid: string,
  addressCountry: string,
}

function nextId(items: { id: number }[]): number {
  return items.reduce((max, item) => Math.max(max, item.id), 0) + 1;
}

function talkingPoint(
  sourceName: string,
  severity: string,
  buyerName: string,
): string {
  return `${sourceName} has a ${severity} finding. ${buyerName} is linked as a potentially affected watch company.`;
}

export function addKnownListAndFlag(
  state: WatchTowerMockState,
  input: {
    findingId: number,
    buyerWatchCompanyId: number,
    watchProductId: number | null,
    flaggedBy?: string,
    addedBy?: string,
    relationKind?: string,
  },
): AddKnownListResult {
  const finding = state.findings.find((item) => item.id === input.findingId);
  if (!finding) {
    return { ok: false, error: 'missing_finding' };
  }
  const source = state.watchCompanies.find((item) => item.id === finding.watchCompanyId);
  const buyer = state.watchCompanies.find((item) => item.id === input.buyerWatchCompanyId);
  if (!source) {
    return { ok: false, error: 'missing_source' };
  }
  if (!buyer) {
    return { ok: false, error: 'missing_buyer' };
  }
  if (buyer.id === source.id) {
    return { ok: false, error: 'same_company' };
  }

  const hasLink = state.knownSupplierLinks.some((link) => (
    link.buyerWatchCompanyId === buyer.id
    && link.supplierWatchCompanyId === source.id
    && link.watchProductId === input.watchProductId
  ));
  const hasFlag = state.findingAffectedCompanies.some((link) => (
    link.findingId === finding.id && link.affectedWatchCompanyId === buyer.id
  ));

  const flaggedBy = input.flaggedBy || 'known_list';
  const addedBy = input.addedBy || MOCK_OPERATOR;
  const relationKind = input.relationKind
    || (input.watchProductId ? 'uses_product' : 'uses_supplier');

  const knownSupplierLinks = hasLink
    ? state.knownSupplierLinks
    : [...state.knownSupplierLinks, {
      id: nextId(state.knownSupplierLinks),
      buyerWatchCompanyId: buyer.id,
      supplierWatchCompanyId: source.id,
      watchProductId: input.watchProductId,
      addedBy,
    }];

  const findingAffectedCompanies = hasFlag
    ? state.findingAffectedCompanies
    : [...state.findingAffectedCompanies, {
      id: nextId(state.findingAffectedCompanies),
      findingId: finding.id,
      sourceWatchCompanyId: source.id,
      affectedWatchCompanyId: buyer.id,
      relationKind,
      watchProductId: input.watchProductId,
      flaggedBy,
      status: 'linked',
      owner: null,
      notifiedAt: null,
      talkingPoint: talkingPoint(source.name, finding.severity, buyer.name),
    }];

  return {
    ok: true,
    createdLink: !hasLink,
    createdFlag: !hasFlag,
    state: {
      ...state,
      knownSupplierLinks,
      findingAffectedCompanies,
    },
  };
}

export function createBuyerAndFlag(
  state: WatchTowerMockState,
  input: {
    findingId: number,
    buyer: NewBuyerInput,
    watchProductId: number | null,
  },
): AddKnownListResult {
  const name = input.buyer.name.trim();
  const uid = input.buyer.uid.trim();
  const country = input.buyer.addressCountry.trim().toUpperCase();
  if (!name) {
    return { ok: false, error: 'missing_name' };
  }
  if (!uid) {
    return { ok: false, error: 'missing_uid' };
  }
  if (!/^[A-Z]{2}$/.test(country)) {
    return { ok: false, error: 'invalid_country' };
  }

  const id = nextWatchCompanyId(state);
  const company = {
    ...createDraftWatchCompany(id),
    name,
    uid,
    addressCountry: country,
    status: 'active' as const,
  };
  const withBuyer: WatchTowerMockState = {
    ...state,
    watchCompanies: [...state.watchCompanies, company],
  };
  return addKnownListAndFlag(withBuyer, {
    findingId: input.findingId,
    buyerWatchCompanyId: id,
    watchProductId: input.watchProductId,
  });
}

function discoveryHint(state: WatchTowerMockState, findingId: number): {
  name: string,
  addressCountry: string,
  reason: string,
} | null {
  const raw = state.discoveryProposals.find((item) => item.findingId === findingId);
  if (!raw) {
    return null;
  }
  const name = typeof raw.name === 'string' ? raw.name : '';
  const addressCountry = typeof raw.addressCountry === 'string' ? raw.addressCountry : '';
  const reason = typeof raw.reason === 'string' ? raw.reason : '';
  if (!name) {
    return null;
  }
  return { name, addressCountry, reason };
}

export function chatsForFinding(
  state: WatchTowerMockState,
  findingId: number,
): WatchIssueChat[] {
  return state.issueChats.filter((chat) => chat.findingId === findingId);
}

export function messagesForChat(
  state: WatchTowerMockState,
  chatId: number,
): WatchIssueChatMessage[] {
  return state.issueChatMessages
    .filter((item) => item.chatId === chatId)
    .sort((left, right) => left.createdAt.localeCompare(right.createdAt));
}

function chatHasExtractedJson(state: WatchTowerMockState, chatId: number): boolean {
  return messagesForChat(state, chatId).some((item) => Boolean(item.extractedJson));
}

export function openOrSeedPathAChat(
  state: WatchTowerMockState,
  finding: WatchFinding,
  now = new Date(),
): { state: WatchTowerMockState, chatId: number } {
  const reusable = chatsForFinding(state, finding.id).find((chat) => (
    chat.seedKind === 'path_a' && !chatHasExtractedJson(state, chat.id)
  ));
  if (reusable) {
    return { state, chatId: reusable.id };
  }

  const chatId = nextId(state.issueChats);
  const stamp = now.toISOString();
  const hint = discoveryHint(state, finding.id);
  const userText = hint
    ? [
      'Which companies potentially use this source?',
      `Discovery hint: ${hint.name} (${hint.addressCountry}).`,
      hint.reason,
    ].join(' ')
    : `Which watch companies potentially use this supplier or product? Finding: ${finding.title}.`;
  const assistantText = hint
    ? `${hint.name} (${hint.addressCountry}): ${hint.reason} Path A seed talk. No import.`
    : 'No discovery seed in the fixture. Type a message and send it.';

  const chat: WatchIssueChat = {
    id: chatId,
    findingId: finding.id,
    title: `Affected Company Discovery — ${finding.title}`,
    agentModelId: null,
    seedKind: 'path_a',
    seedAgentRunId: null,
    createdBy: MOCK_OPERATOR,
    aiChatId: null,
  };
  const userMessageId = nextId(state.issueChatMessages);
  const messages: WatchIssueChatMessage[] = [
    {
      id: userMessageId,
      chatId,
      role: 'user',
      contentText: userText,
      attachedContextJson: {
        findingId: finding.id,
        findingTitle: finding.title,
        severity: finding.severity,
      },
      extractedJson: null,
      createdAt: stamp,
    },
    {
      id: userMessageId + 1,
      chatId,
      role: 'assistant',
      contentText: assistantText,
      attachedContextJson: null,
      extractedJson: null,
      createdAt: stamp,
    },
  ];

  return {
    chatId,
    state: {
      ...state,
      issueChats: [...state.issueChats, chat],
      issueChatMessages: [...state.issueChatMessages, ...messages],
    },
  };
}

export function openOrCreateManualChat(
  state: WatchTowerMockState,
  finding: WatchFinding,
): { state: WatchTowerMockState, chatId: number } {
  const existing = chatsForFinding(state, finding.id);
  if (existing.length) {
    return { state, chatId: existing[existing.length - 1].id };
  }
  const chatId = nextId(state.issueChats);
  const chat: WatchIssueChat = {
    id: chatId,
    findingId: finding.id,
    title: `Issue chat — ${finding.title}`,
    agentModelId: null,
    seedKind: 'manual',
    seedAgentRunId: null,
    createdBy: MOCK_OPERATOR,
    aiChatId: null,
  };
  return {
    chatId,
    state: {
      ...state,
      issueChats: [...state.issueChats, chat],
    },
  };
}

export function appendUserChatMessage(
  state: WatchTowerMockState,
  chatId: number,
  contentText: string,
  now = new Date(),
): WatchTowerMockState {
  const text = contentText.trim();
  if (!text || !state.issueChats.some((item) => item.id === chatId)) {
    return state;
  }
  return {
    ...state,
    issueChatMessages: [...state.issueChatMessages, {
      id: nextId(state.issueChatMessages),
      chatId,
      role: 'user',
      contentText: text,
      attachedContextJson: null,
      extractedJson: null,
      createdAt: now.toISOString(),
    }],
  };
}

export const CHAT_CHIP_KINDS = [
  'finding',
  'source',
  'products',
  'buildings',
  'knownBuyers',
  'otherFindings',
  'pathASeed',
] as const;

export type ChatChipKind = typeof CHAT_CHIP_KINDS[number];

export const CANNED_SEND_TO_AI = cannedSendToAiJson as WatchCompanyImportFile;

export type SendToAiResult =
  | { ok: true, state: WatchTowerMockState, importedUids: string[] }
  | {
    ok: false,
    error: 'missing_chat' | 'missing_finding' | 'invalid_json',
    state: WatchTowerMockState,
    issues?: ImportIssue[],
  };

export function buildChatContextChips(
  state: WatchTowerMockState,
  finding: WatchFinding,
  selected: ChatChipKind[],
): Record<string, unknown> {
  const source = state.watchCompanies.find((item) => item.id === finding.watchCompanyId);
  const snapshot: Record<string, unknown> = {};
  if (selected.includes('finding')) {
    snapshot.finding = {
      publicId: finding.publicId,
      title: finding.title,
      severity: finding.severity,
      description: finding.description,
      payloadJson: finding.payloadJson,
    };
  }
  if (selected.includes('source') && source) {
    snapshot.source = {
      name: source.name,
      addressCountry: source.addressCountry,
      identifiers: source.identifiers,
      naceSection: source.naceSection,
    };
  }
  if (selected.includes('products') && source) {
    snapshot.products = source.products.map((item) => ({
      name: item.name,
      code: item.code,
      hsCode: item.hsCode,
    }));
  }
  if (selected.includes('buildings') && source) {
    snapshot.buildings = source.buildings.map((item) => ({
      name: item.name,
      addressCountry: item.addressCountry,
      addressCity: item.addressCity,
    }));
  }
  if (selected.includes('knownBuyers') && source) {
    snapshot.knownBuyers = state.knownSupplierLinks
      .filter((link) => link.supplierWatchCompanyId === source.id)
      .map((link) => state.watchCompanies.find((item) => item.id === link.buyerWatchCompanyId)?.name)
      .filter(Boolean);
  }
  if (selected.includes('otherFindings') && source) {
    snapshot.otherFindings = state.findings
      .filter((item) => (
        item.watchCompanyId === source.id
        && item.id !== finding.id
        && (item.severity === 'high' || item.severity === 'critical')
      ))
      .map((item) => ({ publicId: item.publicId, title: item.title, severity: item.severity }));
  }
  if (selected.includes('pathASeed')) {
    const pathAChat = chatsForFinding(state, finding.id).find((item) => item.seedKind === 'path_a');
    snapshot.pathASeed = pathAChat
      ? messagesForChat(state, pathAChat.id).map((item) => ({
        role: item.role,
        contentText: item.contentText,
      }))
      : [];
  }
  return snapshot;
}

function companyUid(raw: Record<string, unknown>): string {
  return typeof raw.uid === 'string' ? raw.uid.trim() : '';
}

function appendAssistantTurn(
  state: WatchTowerMockState,
  chatId: number,
  contentText: string,
  attachedContextJson: Record<string, unknown> | null,
  extractedJson: Record<string, unknown> | null,
  now: Date,
): WatchTowerMockState {
  return {
    ...state,
    issueChatMessages: [...state.issueChatMessages, {
      id: nextId(state.issueChatMessages),
      chatId,
      role: 'assistant',
      contentText,
      attachedContextJson,
      extractedJson,
      createdAt: now.toISOString(),
    }],
  };
}

export function sendToAiGenerateImport(
  state: WatchTowerMockState,
  input: {
    chatId: number,
    findingId: number,
    attachedContextJson: Record<string, unknown> | null,
    cannedJson?: unknown,
    now?: Date,
  },
): SendToAiResult {
  const now = input.now || new Date();
  const chat = state.issueChats.find((item) => item.id === input.chatId);
  if (!chat) {
    return { ok: false, error: 'missing_chat', state };
  }
  const finding = state.findings.find((item) => item.id === input.findingId);
  if (!finding) {
    return { ok: false, error: 'missing_finding', state };
  }

  if (chat.findingId !== finding.id) {
    return { ok: false, error: 'missing_finding', state };
  }

  const canned = input.cannedJson ?? CANNED_SEND_TO_AI;
  const text = JSON.stringify(canned, null, 2);
  const report = parseWatchCompanyImportJson(text, 'send-to-ai.json', state);
  if (!report.payload || hasImportErrors(report.issues)) {
    const errorText = report.issues.map((item) => `${item.field}: ${item.message}`).join('\n')
      || 'Invalid canonical import JSON.';
    return {
      ok: false,
      error: 'invalid_json',
      issues: report.issues,
      state: appendAssistantTurn(
        state,
        input.chatId,
        errorText,
        input.attachedContextJson,
        null,
        now,
      ),
    };
  }

  const imported = applyWatchCompanyImport(state, report.payload);
  const importedUids = report.payload.companies
    .map(companyUid)
    .filter(Boolean)
    .filter((uid) => {
      const company = imported.watchCompanies.find((item) => item.uid === uid);
      return Boolean(company && company.id !== finding.watchCompanyId);
    });

  const flagged = importedUids.reduce((acc, uid) => {
    const buyer = acc.watchCompanies.find((item) => item.uid === uid);
    if (!buyer) {
      return acc;
    }
    const result = addKnownListAndFlag(acc, {
      findingId: finding.id,
      buyerWatchCompanyId: buyer.id,
      watchProductId: null,
      flaggedBy: 'issue_chat',
      addedBy: 'chat',
      relationKind: 'likely_buyer',
    });
    return result.ok ? result.state : acc;
  }, imported);

  return {
    ok: true,
    importedUids,
    state: appendAssistantTurn(
      flagged,
      input.chatId,
      text,
      input.attachedContextJson,
      report.payload as unknown as Record<string, unknown>,
      now,
    ),
  };
}
